import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { Button } from '../Button';
import { EmptyState } from '../EmptyState';
import { LoadingSkeleton } from '../LoadingSkeleton';
import { SlideOver } from '../SlideOver';
import { useToast } from '../toastContext';
import { Tabs } from '../Tabs';
import type { NotificationItem } from '../../api/notificationsTypes';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsList,
} from '../../hooks/useNotifications';
import styles from './NotificationSlideOver.module.css';

// WS-4a (C1) — bell slide-over. Lists the caller's most recent
// notifications with a real mark-read + mark-all-read flow. The
// "All / Unread" toggle maps 1:1 to the backend `filter` query
// parameter; clicking a row navigates via the `link` field the
// notification carries (or, if no link, marks-read in place).
//
// Truthful posture: this surface intentionally does NOT show fake
// category pills, fake snooze affordances, or fake email/browser-push
// status — those are persisted in /profile preferences and the
// transports are not yet live.

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export interface NotificationSlideOverProps {
  open: boolean;
  onClose: () => void;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB');
}

export function NotificationSlideOver({
  open,
  onClose,
}: NotificationSlideOverProps): JSX.Element {
  const [tab, setTab] = useState<TabId>('all');
  const navigate = useNavigate();
  const toast = useToast();
  const list = useNotificationsList({ page: 1, filter: tab, limit: 25 });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = list.data?.items ?? [];
  const unreadCount = list.data?.unreadCount ?? 0;
  const tabs = useMemo(() => TABS.slice(), []);

  async function handleRowClick(n: NotificationItem): Promise<void> {
    if (!n.isRead) {
      try {
        await markRead.mutateAsync(n.id);
      } catch {
        // The bell read button is non-critical — surface and continue.
        toast.show({ type: 'error', title: 'Could not mark as read' });
      }
    }
    if (n.link) {
      onClose();
      navigate(n.link);
    }
  }

  async function handleMarkAll(): Promise<void> {
    try {
      const result = await markAll.mutateAsync();
      toast.show({
        type: 'success',
        title:
          result.markedCount > 0
            ? `${result.markedCount} marked as read`
            : 'Nothing to mark',
      });
    } catch {
      toast.show({ type: 'error', title: 'Could not mark all as read' });
    }
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Notifications"
      footer={
        <Button
          variant="outline"
          icon={<CheckCheck size={14} strokeWidth={1.5} aria-hidden="true" />}
          onClick={handleMarkAll}
          disabled={unreadCount === 0 || markAll.isPending}
          data-testid="notification-mark-all"
        >
          {markAll.isPending ? 'Marking…' : 'Mark all as read'}
        </Button>
      }
    >
      <div
        data-testid="notification-slideover"
        className={styles.body}
      >
        <Tabs
          tabs={tabs.map((t) => ({
            id: t.id,
            label: t.label,
            count: t.id === 'unread' ? unreadCount : undefined,
          }))}
          activeId={tab}
          onSelect={(id) => setTab(id as TabId)}
          small
        />

        {list.isError ? (
          <EmptyState
            icon={<Bell size={56} strokeWidth={1.5} aria-hidden="true" />}
            title="Could not load notifications"
            message="Reload the page to retry."
          />
        ) : list.isLoading || !list.data ? (
          <LoadingSkeleton variant="text" lines={4} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Bell size={56} strokeWidth={1.5} aria-hidden="true" />}
            title={tab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          />
        ) : (
          <ul
            className={styles.list}
            data-testid="notification-list"
          >
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  data-testid={`notification-row-${n.id}`}
                  data-unread={n.isRead ? 'false' : 'true'}
                  onClick={() => handleRowClick(n)}
                  className={styles.row}
                  style={{
                    background: n.isRead
                      ? 'transparent'
                      : 'var(--accent-surface, rgba(25, 118, 210, 0.08))',
                  }}
                >
                  <div className={styles.rowHeader}>
                    <span style={{ fontWeight: n.isRead ? 400 : 600 }}>{n.title}</span>
                    <span className={styles.rowTimestamp}>
                      {formatWhen(n.createdAt)}
                    </span>
                  </div>
                  {n.message ? (
                    <span className={styles.rowMessage}>{n.message}</span>
                  ) : null}
                  <span className={styles.rowType}>
                    {n.type}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <p className={styles.footnote}>
          Unread counts refresh every 60&nbsp;seconds. Live push (SSE) lands in a later
          slice along with email and browser-push delivery — toggles in Profile only
          persist your intent today.
        </p>
      </div>
    </SlideOver>
  );
}
